import { createClient } from '@supabase/supabase-js';
import { writeSignedInHint } from './siteSession';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase URL or Anon Key missing in environment variables');
}

const REMEMBER_KEY = 'bricks-remember-me';

// Call before signing in. Default (flag absent or '1') persists to localStorage,
// matching prior behavior. Explicit '0' routes the session to sessionStorage so
// it clears when the browser closes instead of surviving on disk.
// Storage can throw (SecurityError) in a third-party iframe when the browser
// blocks partitioned storage — e.g. the embeddable /v/:visitId Visit on an MLS
// or brokerage site. Fall back to memory so the public page still boots.
const memoryStore = new Map();
function safeStore(kind) {
  try {
    const s = kind === 'session' ? window.sessionStorage : window.localStorage;
    const probe = '__bricks_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return {
      getItem: (k) => (memoryStore.has(k) ? memoryStore.get(k) : null),
      setItem: (k, v) => memoryStore.set(k, String(v)),
      removeItem: (k) => memoryStore.delete(k),
    };
  }
}
const local = safeStore('local');
const session = safeStore('session');

export function setRememberMe(remember) {
  local.setItem(REMEMBER_KEY, remember ? '1' : '0');
}

const pick = () => (local.getItem(REMEMBER_KEY) === '0' ? session : local);
const rememberAwareStorage = {
  getItem: (key) => pick().getItem(key),
  setItem: (key, value) => pick().setItem(key, value),
  removeItem: (key) => pick().removeItem(key),
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: rememberAwareStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Required for supabase.auth.signInWithPasskey / registerPasskey (beta).
    experimental: { passkey: true },
  },
});

// Mirror a boolean "signed in" hint onto .brxs.ca so the www header can show
// "Open Bricks" / "Log out". Never the token: see ./siteSession.js.
// Fires for INITIAL_SESSION too, so existing sessions pick up the hint on their
// next app load and stale hints clear when there is no session.
supabase.auth.onAuthStateChange((_event, session) => {
  writeSignedInHint(!!session, { persistent: local.getItem(REMEMBER_KEY) !== '0' });
});
